import { useTranslation } from 'next-i18next'
import { RefObject, useEffect, useRef } from 'react'

import { CardVariant, IProductCard, ProductCardTag } from '@src/components/product-card/product-card.types'
import { useProductWishList } from '@src/hooks/useProductWishlist'
import {
  getIndividualTagStatus,
  getProductTagsList,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import {
  getProductPropertiesForTracking,
  getProductPropertiesForTrackingNew,
} from '@src/telemetry/getProductPropertiesForTracking'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import Product from '../domains/Product'
import { ProductOptionName } from '../domains/ProductOption'
import ProductTag from '../domains/ProductTag'
import { ProductVariantOption } from '../domains/ProductVariant'
import productHelper from '../helpers/productHelper'
import { EDDDetails } from '../services/eddService/eddService.types'
import { batchedAnalytics } from '../utils/batched-analytics'
import { getValidPromo } from '../utils/festivalOffersUtility'
import { runWhenMainThreadIdle } from '../utils/interaction-scheduling'
import {
  getLegacyProductCardColorVariantsForAnalytics,
  getNewProductCardColorVariantsForAnalytics,
} from '../utils/product-card-color-variant-analytics'

import { generateEDDLabel } from './edd/utils/edd.utils'
import { useWishList } from './useWishList'

export const useBatchedAnalyticsCaptureForProductCard = ({
  productViewTracker,
  wrapperRef,
  product,
  selectedOptions,
  applicableTags,
  isVisible,
}: {
  productViewTracker: string | undefined
  wrapperRef: RefObject<HTMLDivElement>
  product?: Product
  selectedOptions?: ProductVariantOption[]
  applicableTags?: ProductTag[]
  isVisible: boolean
}) => {
  const { isItemInWishList } = useWishList()

  useEffect(() => {
    if (productViewTracker && wrapperRef.current && isVisible && product && selectedOptions && applicableTags) {
      // Cancel the pending idle work if the card unmounts before it fires.
      return runWhenMainThreadIdle(() => {
        const tags = getProductTagsList(applicableTags, selectedOptions)
        const color_variant = getLegacyProductCardColorVariantsForAnalytics(product) ?? []
        batchedAnalytics.pushEventToBatchedAnalyticsStream({
          tracker: productViewTracker,
          event: {
            productId: product.id,
            color: selectedOptions.find(so => so.name === ProductOptionName.Color)?.value,
            productOptions: selectedOptions.map(so => ({ name: so.name, value: so.value })),
            tags: tags,
            tag_ids: transformTagsIntoAnalyticsTags(tags),
            oos_flag: getIndividualTagStatus(tags).sold_out,
            name: product.title,
            color_variant,
            count_color_variant: color_variant.length,
            rating: product.productReviewSummary?.averageRating,
            ...getIndividualTagStatus(tags),
            wishlishted: isItemInWishList(product, selectedOptions),
            spid: product.spid,
            price: productHelper.getQuincePriceForOptions(product, selectedOptions),
            productProperties: getProductPropertiesForTracking(product),
          },
        })
      })
    }

    return undefined
  }, [isVisible, product, selectedOptions, applicableTags, productViewTracker, isItemInWishList, wrapperRef])
}

export const useBatchedAnalyticsCaptureForNewProductCard = ({
  productCard,
  productViewTracker,
  wrapperRef,
  selectedVariant,
  applicableTags,
  isVisible,
  eddDetails,
}: {
  productCard: IProductCard | undefined
  productViewTracker: string | undefined
  wrapperRef: RefObject<HTMLDivElement>
  selectedVariant?: CardVariant
  applicableTags?: ProductCardTag[]
  isVisible: boolean
  eddDetails?: EDDDetails
}) => {
  const { isItemInWishList } = useProductWishList()
  const { t } = useTranslation('common')
  const hasTrackedRef = useRef(false)
  const { zipCode } = useZipCode()
  const eddReady = zipCode == null || eddDetails?.edd_type != null
  useEffect(() => {
    if (
      productViewTracker &&
      wrapperRef.current &&
      isVisible &&
      productCard &&
      selectedVariant &&
      applicableTags &&
      eddReady &&
      !hasTrackedRef.current
    ) {
      hasTrackedRef.current = true
      runWhenMainThreadIdle(() => {
        const tags = applicableTags.map(productTag => productTag.value as ProductTagKey)
        const selectProductOptions = [
          { name: selectedVariant.displayConfig.name, value: selectedVariant.displayConfig.value },
        ]
        const spid = productCard.product.relatedIds.spid
        const promoForVariant = getValidPromo(selectedVariant.promoConfig)
        const propertyOverrides = promoForVariant?.promoPrice
          ? { discounted_price: promoForVariant.promoPrice ?? productCard.product.cardVariants[0]?.price }
          : {}
        const color_variant =
          getNewProductCardColorVariantsForAnalytics(
            productCard.product.cardVariants,
            productCard.product.colorVariants,
          ) ?? []
        batchedAnalytics.pushEventToBatchedAnalyticsStream({
          tracker: productViewTracker,
          event: {
            /**
             * Handling the productId as it can come as string from BFF/SAND
             * Analytics expects the productId to be a number
             *  */
            productId: isNaN(Number(productCard.product.productId))
              ? productCard.product.productId
              : Number(productCard.product.productId),
            color: selectedVariant.displayConfig.value,
            productOptions: selectProductOptions,
            tags,
            tag_ids: transformTagsIntoAnalyticsTags(tags),
            oos_flag: getIndividualTagStatus(tags).sold_out,
            name: productCard.product.title,
            color_variant,
            count_color_variant: color_variant.length,
            rating: productCard.product.reviews.averageRating,
            ...getIndividualTagStatus(tags),
            wishlishted: isItemInWishList(spid, selectedVariant.displayConfig.value),
            spid: spid,
            productProperties: getProductPropertiesForTrackingNew({
              product: productCard.product,
              productVariant: selectedVariant,
              tags,
              propertyOverrides,
            }),
            price: selectedVariant.price?.salePrice,
            edd_standard_eligible: eddDetails?.type === 'LB_STANDARD',
            edd_express_eligible: eddDetails?.type === 'LB_SUPER',
            edd_max_days: eddDetails?.max_days,
            edd_min_days: eddDetails?.min_days,
            edd_max_date: eddDetails?.max_date,
            edd_min_date: eddDetails?.min_date,
            location_ids: eddDetails?.inventoryLocations,
            edd_label: generateEDDLabel(eddDetails, t),
            is_app_exclusive: selectedVariant.displayConfig.appExclusive ? 1 : 0,
            is_final_sale: promoForVariant?.isFinalSale ? 1 : 0,
            zip_code: zipCode,
            edd_type: eddDetails?.edd_type,
            type: eddDetails?.type,
          },
          retryCount: 3,
        })
      })
    }
  }, [
    isVisible,
    productCard?.product.productId,
    productCard,
    selectedVariant,
    applicableTags,
    productViewTracker,
    isItemInWishList,
    wrapperRef,
    eddDetails,
    eddReady,
    t,
    zipCode,
  ])
}
